import Effects from './Effects.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import Tone from 'tone';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {AbstractAudioParamParameter,
        AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import {decibelsToLinear, linearToDecibels} from 'utils/db.js';

const SIZE = 65536;
let curve = new Float32Array(SIZE);

class WaveFolder extends Effects.Effect {

    constructor(sequencerEngine) {
        super('Wave Folder', () => new Tone.Gain(1), sequencerEngine); 

        this.counter=0;
        this.waveShaperNode = new Tone.WaveShaper([-1, 1]);
        this.effectNode.connect(this.waveShaperNode);
        this.postGain = new Tone.Gain(1);
        this.waveShaperNode.connect(this.postGain);

        this.overSampleParameter = new AbstractParameter(
            "oversample", this, (obj, x) => this.waveShaperNode.oversample = x ? '4x' : 'none', {
                min: 0.01,
                max: 32,
                unit: '',
                defaultValue: false,
                constantAutomationRate: true
            });

        this.driveParameter = new AbstractAudioParamParameter(
            'drive', this.effectNode.gain, (obj, drive) => {
                this.effectNode.gain.value = drive;
                this.postGain.gain.value = Math.pow(1 / drive, 0.6);
            },
            {
                documention: "Controls the level of saturation.",
                min: 0.01,
                max: 32,
                unit: '',
                defaultValue: 1,
                constantAutomationRate: true
            });
        this.registerParameter(this.driveParameter);

        this.stagesParameter = new AbstractParameter(
            'stages', this, (obj, drive) => {
                this.recomputeCurve();
            },
            {
                documention: "Controls the level of saturation.",
                min: 2,
                max: 10,
                unit: '',
                defaultValue: 4,
                constantAutomationRate: true,
                automatable: false
            });
        this.registerParameter(this.stagesParameter);

        this.smoothnessParameter = new AbstractParameter(
            'smoothness', this, (obj, drive) => {
                this.recomputeCurve();
            },
            {
                documention: "Controls the level of saturation.",
                min: 1,
                max: 32,
                unit: '',
                defaultValue: 6,
                constantAutomationRate: true,
                automatable: false
            });
        this.registerParameter(this.smoothnessParameter);
        this.updateInitialValues();
    }

    get stages() {
        return this.stagesParameter ? this.stagesParameter.get() : 4;
    }

    get smoothness() {
        return this.smoothnessParameter ? this.smoothnessParameter.get() : 6;
    }
   
    recomputeCurve( ){
        let c = ++this.counter;
        setTimeout(() => { 
            if (c !== this.counter) {
                return;
            }
            const points = Math.pow(2, this.smoothness+1)*(2*this.stages + 1) + 1
            const curve = new Float32Array(points);
            for (let i = 0; i < points; i++) {
                const x = (2*this.stages + 1)*Math.PI*(i/(points-1) - 0.5);
                curve[i] = Math.sin(x);
            }
            
            this.waveShaperNode.curve = curve;
        }, 100);
        //inputGainLow = 1.0/(points-1);
        // TODO: get inputGainNode gain based on inputGainLow and current Amount setting
    }
    
    get documentation() {
        return "Saturates the sound, making it more distorted.";
    }

    dispose() {
        super.dispose();
        this.postGain.dispose();
        this.waveShaperNode.dispose();
    }

    connect(node) {
        this.postGain.connect(node);
    }

    disconnect(node) {
        this.postGain.disconnect(node);
    }

    getJson() {
        let json = {
            ... super.getJson(),
            oversample: this.overSampleParameter.get(),
            smoothness: this.smoothnessParameter.get(),
            stages: this.stagesParameter.get(),
            drive: this.driveParameter.get()
        };

        json = this.saveParameterToJson(json, "oversampleParameter", this.overSampleParameter);
        json = this.saveParameterToJson(json, "driveParameter", this.driveParameter);
        json = this.saveParameterToJson(json, "smoothnessParameter", this.smoothnessParameter);
        json = this.saveParameterToJson(json, "stagesParameter", this.stagesParameter);
        return json;
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        bulkUpdate(this.driveParameter, json.drive, bulk);
        bulkUpdate(this.overSampleParameter, json.overSample, bulk);
        bulkUpdate(this.smoothnessParameter, json.smoothness, bulk);
        bulkUpdate(this.stagesParameter, json.stages, bulk);
        return {
            ... bulk,
            ... this.loadParameterFromJson(json, 'driveParameter', this.driveParameter, isCopy),
            ... this.loadParameterFromJson(json, 'overSampleParameter', this.overSampleParameter, isCopy),
            ... this.loadParameterFromJson(json, 'smoothnessParameter', this.smoothnessParameter, isCopy),
            ... this.loadParameterFromJson(json, 'stagesParameter', this.stagesParameter, isCopy),
        };
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var copy = new WaveFolder(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }
}

export default WaveFolder;
